/**
 * PC Pawn Color Schemes
 *
 * Color definitions for the retro computer-themed pawn design.
 * Each pawn is a character body topped with a CRT monitor.
 * Colors are extracted from the per-color SVG assets in public/ia-docs/skins/PC/Pion/.
 */

export interface PcPawnColorScheme {
  /** Unique name used as gradient ID prefix to avoid SVG collisions */
  name: string;

  /** Main body highlight fill (bright accent) */
  bodyHighlight: string;

  /** Body glow/face circle fill */
  bodyGlow: string;

  /** Accent elements fill (monitor LED, USB ports, power button) */
  accent: string;

  /** Dark fill (cursor arrow, polyline strokes) */
  darkFill: string;

  /** Darker fill (inner body ellipse) */
  darkerFill: string;

  /** Polyline stroke color */
  strokeColor: string;

  /** Monitor screen frame color */
  screenFrame: string;

  /** Body outer gradient: 5-stop dark gradient [start, s2, s3, s4, end] */
  bodyOuterGradient: [string, string, string, string, string];

  /** Body inner gradient: 5-stop dark gradient */
  bodyInnerGradient: [string, string, string, string, string];

  /** Body shadow gradient: 3-stop */
  bodyShadowGradient: [string, string, string];

  /** Shirt gradient: 6-stop bright gradient */
  shirtGradient: [string, string, string, string, string, string];

  /** Screen dark gradient: 4-stop */
  screenGradient: [string, string, string, string];
}

export const PC_PAWN_COLOR_SCHEMES: Record<string, PcPawnColorScheme> = {
  blue: {
    name: 'blue',
    bodyHighlight: '#707fef',
    bodyGlow: '#7086ef',
    accent: '#005fea',
    darkFill: '#111175',
    darkerFill: '#12174c',
    strokeColor: '#111175',
    screenFrame: '#767884',
    bodyOuterGradient: ['#001372', '#0707aa', '#050d6b', '#081d84', '#0a0a54'],
    bodyInnerGradient: ['#000e72', '#0723aa', '#05176b', '#081d84', '#0a1a54'],
    bodyShadowGradient: ['#101972', '#0a1454', '#0f1d6d'],
    shirtGradient: ['#555eff', '#525cfb', '#4c57f2', '#414fe1', '#3243ca', '#2237b2'],
    screenGradient: ['#000e42', '#000c39', '#000721', '#000000']
  },
  red: {
    name: 'red',
    bodyHighlight: '#ef7070',
    bodyGlow: '#ef7070',
    accent: '#ed0000',
    darkFill: '#771010',
    darkerFill: '#4f1212',
    strokeColor: '#771010',
    screenFrame: '#877777',
    bodyOuterGradient: ['#720000', '#aa0707', '#6b0505', '#840808', '#540a0a'],
    bodyInnerGradient: ['#720000', '#aa0707', '#6b0505', '#840808', '#540a0a'],
    bodyShadowGradient: ['#721010', '#540a0a', '#6d0f0f'],
    shirtGradient: ['#ff556a', '#fb5267', '#f24c5d', '#e1414e', '#ca3238', '#b22222'],
    screenGradient: ['#440000', '#3b0000', '#230000', '#000000']
  },
  green: {
    name: 'green',
    bodyHighlight: '#70f2ca',
    bodyGlow: '#70f2ca',
    accent: '#1ea488',
    darkFill: '#0e7c62',
    darkerFill: '#125136',
    strokeColor: '#0e7c62',
    screenFrame: '#798c83',
    bodyOuterGradient: ['#00755f', '#05ad75', '#056d59', '#07875c', '#095642'],
    bodyInnerGradient: ['#00754e', '#05ad79', '#056d59', '#07876e', '#095647'],
    bodyShadowGradient: ['#107566', '#095552', '#0f705a'],
    shirtGradient: ['#52ffbd', '#4ffbb9', '#49f2b0', '#3ee19f', '#2fca88', '#22b573'],
    screenGradient: ['#004530', '#003b2a', '#002318', '#000000']
  },
  yellow: {
    name: 'yellow',
    bodyHighlight: '#efe670',
    bodyGlow: '#efe370',
    accent: '#efe800',
    darkFill: '#7a7a0f',
    darkerFill: '#4f4f12',
    strokeColor: '#7a7a0f',
    screenFrame: '#898877',
    bodyOuterGradient: ['#727200', '#aaaa07', '#686b05', '#848008', '#54520a'],
    bodyInnerGradient: ['#727200', '#aaa507', '#6b6805', '#847808', '#54520a'],
    bodyShadowGradient: ['#727210', '#54540a', '#6d680f'],
    shirtGradient: ['#fffa55', '#fbf752', '#f2ed4c', '#e1de41', '#cac832', '#b2b222'],
    screenGradient: ['#474500', '#3e3c00', '#262500', '#020200']
  }
};
